<!-- Overrides @basecom-gmbh/pond/components/Layout/LayoutFooter.vue -->
<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { getTranslatedProperty, getCategoryRoute } from '@shopware-pwa/helpers-next';
import SharedNewsletterRegistration from '../Shared/SharedNewsletterRegistration.vue';

const { navigationElements, loadNavigationElements } = useNavigation({ type: 'footer-navigation' });
const { navigationElements: serviceNavigationElements, loadNavigationElements: loadServiceElements } = useNavigation({
    type: 'service-navigation',
});

onMounted(async () => {
    await loadNavigationElements({ depth: 1 });
    await loadServiceElements({ depth: 1 });
});
</script>

<template>
    <!-- IDS-48 - Change colors-->
    <div class="mt-auto">
        
<FormKitLazyProvider config-file="true">
<footer class="mt-20 bg-brand-primary md:mt-28">
            <div class="container space-y-10 py-20 lg:pb-11 lg:pt-20">
                <div class="gap-10 lg:flex">
                    <!-- large logo with introduction text-->
                    <div class="gap-10 md:flex">
                        <LayoutLogo logo-classes="h-44" />

                        <div class="max-w-80">
                            <h3 class="mb-2 mt-4 text-xl font-bold leading-6 md:mt-0 lg:text-3xl">
                                {{ $t('global.footer.shopName') }}
                            </h3>
                            <p class="leading-6">
                                {{ $t('global.footer.shopDescription') }}
                            </p>
                        </div>
                    </div>

                    <!-- newsletter -->
                    <div class="mt-10 max-w-[26rem] lg:mt-0">
                        <h3 class="mb-2 text-xl font-bold leading-6 lg:text-3xl">
                            {{ $t('global.footer.newsletter.headline') }}
                        </h3>
                        <p class="leading-6">
                            {{ $t('global.footer.newsletter.description') }}
                        </p>

                        <SharedNewsletterRegistration />
                    </div>
                </div>

                <!-- footer navigation -->
                <div
                    class="space-y-10 text-md md:mt-10 md:grid md:grid-cols-4 md:items-baseline md:justify-normal md:gap-8 md:space-y-0 md:border-t md:border-neutral-800/20 md:pt-10"
                >
                    <template
                        v-for="navigationElement in navigationElements"
                        :key="navigationElement.id"
                    >
                        <ul class="list-none space-y-2">
                            <li class="max-w-max font-bold hover:text-brand-primary hover:underline">
                                <NuxtLink
                                    v-if="navigationElement.type !== 'folder'"
                                    :target="
                                        navigationElement.externalLink || navigationElement.linkNewTab ? '_blank' : ''
                                    "
                                    :to="getCategoryRoute(navigationElement)"
                                >
                                    {{ getTranslatedProperty(navigationElement, 'name') }}
                                </NuxtLink>
                                <span v-else>
                                    {{ getTranslatedProperty(navigationElement, 'name') }}
                                </span>
                            </li>
                            <template v-if="navigationElement.visibleChildCount > 0">
                                <li
                                    v-for="navigationChild in navigationElement.children"
                                    :key="navigationChild.id"
                                >
                                    <NuxtLink
                                        :target="
                                            navigationChild.externalLink || navigationChild.linkNewTab ? '_blank' : ''
                                        "
                                        :to="getCategoryRoute(navigationChild)"
                                        class="font-normal"
                                    >
                                        {{ getTranslatedProperty(navigationChild, 'name') }}
                                    </NuxtLink>
                                </li>
                            </template>
                            <!-- v-html is necessary because the text contains inline stylings -->
                            <!-- eslint-disable vue/no-v-html -->
                            <li
                                v-else-if="navigationElement.description !== null"
                                v-html="getTranslatedProperty(navigationElement, 'description')"
                            />
                        </ul>
                    </template>
                </div>

                <div class="items-center md:flex md:justify-between md:border-t md:border-neutral-800/20 md:pt-10">
                    <div class="md:flex md:gap-10">
                        <!-- social media links -->
                        <div class="mb-4 flex gap-8 lg:mb-0">
                            <NuxtLink
                                to="https://www.linkedin.com/company/ids-online-gmbh?originalSubdomain=de"
                                target="_blank"
                            >
                                <FormKitIcon
                                    icon="linkedin"
                                    class="block h-6 w-6"
                                />
                            </NuxtLink>
                            <NuxtLink
                                to="https://www.facebook.com/ids.online.gmbh"
                                target="_blank"
                            >
                                <FormKitIcon
                                    icon="facebook"
                                    class="block h-6 w-6"
                                />
                            </NuxtLink>
                        </div>

                        <!-- footer service navigation -->
                        <ul class="flex list-none flex-wrap space-x-1 text-md font-normal">
                            <span>
                                {{ $t('global.footer.copyright') }}
                            </span>
                            <li
                                v-for="navigationElement in serviceNavigationElements"
                                :key="navigationElement.id"
                                class="space-x-1"
                            >
                                <span>I</span>
                                <!-- separator between the links -->
                                <NuxtLink
                                    :target="
                                        navigationElement.externalLink || navigationElement.linkNewTab ? '_blank' : ''
                                    "
                                    :to="getCategoryRoute(navigationElement)"
                                    class="underline"
                                >
                                    {{ getTranslatedProperty(navigationElement, 'name') }}
                                </NuxtLink>
                            </li>
                            <ClientOnly>
                                <span>I</span>
                                <CookieBannerModal>
                                    <template #trigger>
                                        <div class="underline">
                                            {{ $t('global.footer.cookiePreferences') }}
                                        </div>
                                    </template>
                                </CookieBannerModal>
                            </ClientOnly>
                        </ul>
                    </div>

                    <LayoutFooterActions />
                </div>
            </div>
        </footer>
</FormKitLazyProvider>

    </div>
</template>
